<script setup lang="ts">
import { DOC_TYPE_LABELS } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AdpDocumentCardSkeleton from '@/components/common/adp-document/adp-document-card-skeleton.vue';
import ThemePreviewAdpDocument from '@/components/theme/edit/theme-preview-adp-document.vue';
import { DocType, ThemePreviewItem } from '@/types';
import { docTypeDisplayNameMap } from '@/utils/adpDocument';
import { formatDayOfWeek } from '@/utils/formatters';

defineProps<{
  isFetched: boolean;
  isForeignFeedAvailable: boolean;
  isForeignMediaEnabled: boolean;
  isHit: boolean;
  docTypes: DocType[];
  docType: DocType;
  feedType: 'ja' | 'en';
  hitCount: number;
  themePreviewDocumentList: ThemePreviewItem[];
  enableTranslatedDocumentTitle: boolean;
  isNoSiteCategoriesAndExcludeAllianceMedia: boolean;
  hasKeywords: boolean;
  isCompact?: boolean;
}>();

const docTypeDisplayNameMapForThemePreview = {
  ...docTypeDisplayNameMap,
  article: 'ニュース',
};
</script>

<template>
  <div
    v-if="
      docType === 'patent' &&
      isForeignFeedAvailable &&
      isForeignMediaEnabled &&
      feedType === 'en'
    "
    class="o-theme-preview__nohit c-text-regular--m"
  >
    海外特許の配信は行っておりません。
  </div>
  <div
    v-else-if="docType !== 'article' && !docTypes.includes(docType)"
    class="o-theme-preview__nohit c-text-regular--m"
  >
    「表示する技術文献」の{{
      docTypeDisplayNameMapForThemePreview[docType]
    }}にチェックが入っていません。チェックを入れることで参考記事を閲覧することができます。
  </div>
  <div v-else-if="isFetched">
    <div
      class="o-theme-preview__nohit"
      v-if="
        isForeignFeedAvailable && feedType === 'en' && !isForeignMediaEnabled
      "
    >
      <div class="c-text-regular--m">
        「海外メディアの情報を収集する」にチェックが入っていません。チェックを入れることで参考記事を閲覧することができます。
      </div>
    </div>
    <div class="o-theme-preview__hit" v-if="isHit">
      <div
        v-if="!isCompact"
        class="c-text-regular--s hit-count-per-doctype-wrapper"
      >
        {{
          `${feedType === 'ja' ? '国内' : '海外'}${DOC_TYPE_LABELS[docType]}配信数/週`
        }}
        <span class="hit-count-per-doctype">
          ・{{ hitCount.toLocaleString() }}件
        </span>
        <DgrIcon
          size="small"
          name="question-circle"
          v-tooltip.top="{
            content: '直近12週間の1週間あたりの平均配信数を表示します。',
          }"
        />
      </div>
      <div
        class="o-theme-preview__content"
        v-for="preview in themePreviewDocumentList"
        :key="preview.date"
      >
        <div v-if="docType === 'article'" class="adp-document-date__content">
          <div class="adp-document-date__text c-text-regular--s">
            {{ formatDayOfWeek(preview.date) }}
          </div>
        </div>
        <div
          v-for="adpDocument in preview.adpDocuments"
          :key="adpDocument.url"
          :class="[
            `theme-preview-item ${adpDocument.doc_type}`,
            { compact: isCompact },
          ]"
        >
          <ThemePreviewAdpDocument
            :adp-document="adpDocument"
            :is-compact="isCompact"
          ></ThemePreviewAdpDocument>
        </div>
      </div>
    </div>
    <div
      class="o-theme-preview__nohit"
      v-else-if="isNoSiteCategoriesAndExcludeAllianceMedia"
    >
      <div class="c-text-regular--m">
        表示するサイト種別で1つ以上項目を選択してください。
      </div>
    </div>
    <div class="o-theme-preview__nohit" v-else-if="hasKeywords && !isHit">
      <div class="c-text-regular--m">
        設定しているキーワードに一致する情報は見つかりませんでした。
      </div>
    </div>
    <div class="o-theme-preview__nohit" v-else-if="!hasKeywords">
      <div class="c-text-regular--m">キーワードが設定されていません。</div>
    </div>
  </div>
  <AdpDocumentCardSkeleton
    v-else
    class="adp-document-card-skelton"
    :loading="!isFetched"
  ></AdpDocumentCardSkeleton>
</template>

<style scoped lang="scss">
.hit-count-per-doctype-wrapper {
  display: flex;
  .icon-box {
    margin-left: 4px;
  }
}

.o-theme-preview__content {
  display: flex;
  flex-direction: column;
  margin-top: 16px;

  .adp-document-date__content {
    display: flex;
    align-items: center;

    .adp-document-date__text {
      flex-shrink: 0;
      box-shadow: 0 1px 3px rgba(74, 74, 74, 0.4);
      border-radius: 4px;
      padding: 4px;
      width: 56px;
      text-align: center;
      text-wrap: nowrap;
    }
  }

  .adp-document-date__content::before,
  .adp-document-date__content::after {
    content: '';
    flex-grow: 1;
    height: 1px;
    background: $color-gray400;
  }
}

.theme-preview-item.article {
  margin-top: 8px;

  &.compact {
    padding-bottom: 8px;
  }
}

.theme-preview-item.article:not(:last-child) {
  border-bottom: 1px solid $color-gray400;
}

.theme-preview-item.research_paper,
.theme-preview-item.patent {
  padding-top: 8px;
  border-top: 1px solid $color-gray400;
}

.hit-count-per-doctype {
  color: $color-gray800;
}
</style>
